import _defaultTo from 'lodash/defaultTo';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
// Serialization
import { deserialize, serialize } from 'serializr';
// Fragments
import { accountFragment, accountFragmentForGlobalList } from './fragments';
// Models
import mCarpark from './carpark';
// Custom
import { accountStatusPalette } from '../../components/core/colors';
import {
  signUpSchema,
  accountSchema,
  accountSetStatusSchema,
} from './schemas';
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';

/**
 * Serialization
 */
const serializeAccount = ({
  existingAccount,
  newAccount,
  token,
}) => ({
  ...serialize(signUpSchema, {
    ...(existingAccount && {
      existingAccount: {
        ...(!_isNil(existingAccount.email) && { email: existingAccount.email }),
        ...(!_isNil(existingAccount.password) && {
          password: existingAccount.password,
        }),
      },
    }),
    ...(newAccount && {
      newAccount: {
        ...(!_isNil(newAccount.email) && { email: newAccount.email }),
        ...(!_isNil(newAccount.firstname) && {
          firstname: newAccount.firstname,
        }),
        ...(!_isNil(newAccount.lastname) && { lastname: newAccount.lastname }),
        ...(!_isNil(newAccount.locale) && { locale: newAccount.locale }),
        ...(!_isNil(newAccount.password) && { password: newAccount.password }),
        ...(!_isNil(newAccount.passwordConfirmation) && {
          passwordConfirmation: newAccount.passwordConfirmation,
        }),
        ...(!_isNil(newAccount.phone) && { phone: newAccount.phone }),
      },
    }),
    ...(!_isNil(token) && { token }),
  }),
});
const serializeSetStatus = ({
  authorizationId,
  cause,
  comments,
  id,
  status,
}) => ({
  ...serialize(accountSetStatusSchema, {
    authorizationId: _defaultTo(authorizationId, null),
    cause: _defaultTo(_toUpper(cause), null),
    comments: _defaultTo(comments, null),
    ...(!_isNil(id) && { id }),
    ...(!_isNil(status) && { status: _toUpper(status) }),
  }),
});
const deserializeAccount = (data) => deserialize(accountSchema, data);

/**
 * Lists
 */
const accountStatusList = [
  {
    key: 'active',
    value: 'active',
    labelId: 'account.status.active',
    icon: 'accountStatusActive',
    color: accountStatusPalette.active,
  },
  {
    key: 'inactive',
    value: 'inactive',
    labelId: 'account.status.inactive',
    icon: 'accountStatusInactive',
    color: accountStatusPalette.inactive,
  },
  {
    key: 'pending',
    value: 'pending',
    labelId: 'account.status.pending',
    icon: 'accountStatusPending',
    color: accountStatusPalette.pending,
  },
  {
    key: 'mixed',
    value: 'mixed',
    labelId: 'account.status.mixed',
    icon: 'accountStatusMixed',
    color: accountStatusPalette.mixed,
  },
];

const accountCauseList = [
  {
    key: 'bad_affiliate_site',
    value: 'bad_affiliate_site',
    labelId: 'account.cause.badAffiliateSite',
  },
  {
    key: 'end_of_lease',
    value: 'end_of_lease',
    labelId: 'account.cause.endOfLease',
  },
  {
    key: 'fraud',
    value: 'fraud',
    labelId: 'account.cause.fraud',
  },
  {
    key: 'invalid_subscription',
    value: 'invalid_subscription',
    labelId: 'account.cause.invalidSubscription',
  },
];

const validateAccountTypes = {
  accountValidation: 'account_validation',
  customer: 'customer',
};

/**
 * Getters
 */
// Status
const accountStatusActive = accountStatusList[0];
const accountStatusInactive = accountStatusList[1];
const accountStatusPending = accountStatusList[2];
const accountStatusMixed = accountStatusList[3];
// Cause
const accountCauseFraud = accountCauseList[2];

/**
 * Utils
 */
// Status
const accountStatus = (account) => findInList(accountStatusList, 'value', account, 'status');
const accountIsActive = (account) => accountStatus(account).value === accountStatusActive.value;
const accountIsInactive = (account) => accountStatus(account).value === accountStatusInactive.value;
const accountIsPending = (account) => accountStatus(account).value === accountStatusPending.value;
const accountIsAllAccess = (account) => !!_find(account.authorizations, { carpark: null });
const accountGetFinalStatus = (account) => {
  // If one authorization is pending the final status is pending
  if (_find(account.authorizations, { status: accountStatusPending.value })) {
    return accountStatusPending.value;
  }
  // If all authorizations are ACTIVE the final status is ACTIVE
  if (
    _isEmpty(
      _reject(account.authorizations, { status: accountStatusActive.value }),
    )
  ) {
    return accountStatusActive.value;
  }
  // If all authorizations are INACTIVE the final status is INACTIVE
  if (
    _isEmpty(
      _reject(account.authorizations, { status: accountStatusInactive.value }),
    )
  ) {
    return accountStatusInactive.value;
  }
  return accountStatusMixed.value;
};
// Cause
const accountCause = (account) => findInList(accountCauseList, 'value', account, 'cause');
const accountGetCarparkAccess = (account) => {
  if (account.authorizations.length > 1) {
    const listCarparkPublicName = _map(account.authorizations, ({ carpark }) => mCarpark.publicName(carpark));
    return listCarparkPublicName.join(' - ');
  } if (_isEmpty(account.authorizations)) {
    return '';
  }
  return mCarpark.publicName(account.authorizations[0].carpark);
};

/**
 * GraphQL
 */
const queListAccounts = `
 query ListAccounts($pagination: PaginationInput, $search: ListAccountsSearchInput) {
  listAccounts(pagination: $pagination, search: $search) {
     entries {
      ...accountFragmentForGlobalList,
    },
     pageNumber,
     pageSize,
     totalEntries,
     totalPages
   }
 }

 ${accountFragmentForGlobalList}
`;

const mutSignUp = `
  mutation SignUp($input: SignUpInput!) {
    signUp(input: $input) {
      ...accountFragment,
    }
  }

 ${accountFragment}
`;

const mutSetAccountStatus = `
  mutation SetAccountStatusInput($input: SetAccountStatusInput!) {
    setAccountStatus(input: $input) {
      ...accountFragment
    }
  }

  ${accountFragment}
`;

const mutCheckCustomerAccountLink = `
  mutation CheckCustomerAccountLink($input: CheckCustomerAccountLinkInput!) {
    checkCustomerAccountLink(input: $input)
  }
`;

const mutValidateCustomerAccountLink = `
  mutation ValidateCustomerAccountLink($input: ValidateCustomerAccountLinkInput!) {
    validateCustomerAccountLink(input: $input)
  }
`;

const mutValidateAccount = `
  mutation ValidateAccount($input: ValidateAccountInput!) {
    validateAccount(input: $input)
  }
`;

const mutArchiveAccount = `
  mutation ArchiveAccount($input: ArchiveAccountInput!) {
    archiveAccount(input: $input) {
      id
    }
  }
`;

const mutDeleteAccount = `
  mutation DeleteAccount($input: DeleteAccountInput!) {
    deleteAccount(input: $input)
  }
`;

/**
 * Model
 */
const account = {
  name: 'account',
  icon: 'accountList',

  // Serialization
  serialize: serializeAccount,
  serializeSetStatus,
  deserialize: deserializeAccount,

  // Lists
  causeList: accountCauseList,
  statusList: accountStatusList,
  validateAccountTypes,

  // Getters
  getStatusActive: accountStatusActive,
  getStatusInactive: accountStatusInactive,
  getStatusPending: accountStatusPending,
  getStatusMixed: accountStatusMixed,
  getCauseFraud: accountCauseFraud,

  // Utils
  cause: accountCause,
  status: accountStatus,
  isActive: accountIsActive,
  isAllAccess: accountIsAllAccess,
  isInactive: accountIsInactive,
  isPending: accountIsPending,
  getCarparkAccess: accountGetCarparkAccess,
  getFinalStatus: accountGetFinalStatus,

  // List Account features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 3,
      sm: 3,
      md: 3,
      lg: 6,
      xl: 6,
    }),
    columns: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'creationDate',
        labelId: 'field.creationDate',
        field: 'creationDate',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'member',
        labelId: 'field.member',
        field: 'member',
        sortable: false,
      },
      {
        key: 'customerAccess',
        labelId: 'field.customerAccess',
        field: 'customerAccess',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'history',
        labelId: 'field.history',
        field: 'history',
        sortable: false,
        className: 'hidden-md-down table-content-1 table-content-center',
      },
      {
        key: 'actions',
        labelId: 'field.actions',
        field: 'actions',
        sortable: false,
        className: 'table-content-center',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'status',
        },
      },
    ],
  },

  // GraphQL
  queListAccounts,
  mutSignUp,
  mutSetAccountStatus,
  mutCheckCustomerAccountLink,
  mutValidateCustomerAccountLink,
  mutValidateAccount,
  mutArchiveAccount,
  mutDeleteAccount,
};

module.exports = account;



// WEBPACK FOOTER //
// ./models/cpm/account.js